import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import ConfirmModal from 'components/common/ConfirmModal';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import StatusHeader from 'components/common/StatusHeader';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError, TARGET_MENU_SERVICE } from 'utils';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import SelectTargetMenuModal from 'components/SelectTargetMenuModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import ServiceMenuListTable from '../components/ServiceMenuListTable';
import { ServiceMenuAddPayload, ServiceMenuListItem } from '../models';
import serviceMenuListService, { default as serviceMenuService } from '../serviceMenuService';
import { selectServiceMenuData, serviceMenuActions } from '../serviceMenuSlice';
type ServiceMenuListFormValues = {
  serviceMenu: ServiceMenuListItem[];
};
interface ServiceMenuProps {
  targetSourceType?: number;
}
function ServiceMenu({ targetSourceType = TARGET_MENU_SERVICE }: ServiceMenuProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();
  const dispatch = useAppDispatch();

  // ref
  const btnAddRef = useRef<HTMLButtonElement>(null);
  const btnDeleteRef = useRef<HTMLButtonElement>(null);
  const btnSaveRef = useRef<HTMLButtonElement>(null);
  // state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // selector
  const serviceMenuList = useAppSelector(selectServiceMenuData);
  const isEditMode = useAppSelector(selectIsEditMode);

  useEffect(() => {
    if (btnAddRef.current) btnAddRef.current.disabled = false;
    if (btnDeleteRef.current) btnDeleteRef.current.disabled = true;
    if (btnSaveRef.current) btnSaveRef.current.disabled = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //Store List - Hook Form
  const { handleSubmit } = useForm<ServiceMenuListFormValues>({
    defaultValues: {
      serviceMenu: [],
    },
    mode: 'onBlur',
  });

  // API
  function loadServiceMenuListData() {
    try {
      (async () => {
        try {
          // reset selected row
          dispatch(serviceMenuActions.unselectAll());

          const response = await serviceMenuService.getServiceMenuList();
          const serviceMenuListMap = new Map(serviceMenuList.map((item) => [item.id, item.temp_delete_flg]));
          const newServiceMenuList = response.data.map((item: ServiceMenuListItem) => ({
            ...item,
            temp_delete_flg: serviceMenuListMap.has(item.id) ? serviceMenuListMap.get(item.id) : item.delete_flg,
          }));
          dispatch(serviceMenuActions.setServiceMenuList(newServiceMenuList));
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }
  useEffect(() => {
    loadServiceMenuListData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleAddServiceMenuModal() {
    setIsModalOpen(true);
  }
  function handleCloseSelectTargetMenuModal() {
    setIsModalOpen(false);
  }
  // Delete modal function
  async function processDelete() {
    const chkSelectionElems = document.querySelectorAll<HTMLInputElement>('.chk-servicemenu-item > input:checked');
    if (chkSelectionElems.length > 0) {
      //compose delete data
      const payload = { ids: [] as number[] };
      let updatedServiceMenuList = [...serviceMenuList];
      chkSelectionElems.forEach((element) => {
        if (element.dataset.type === 'servicemenu') {
          const serviceMenuId = Number(element.value);
          payload.ids.push(serviceMenuId);
          updatedServiceMenuList = updatedServiceMenuList.map((item) =>
            item.id === serviceMenuId ? { ...item, delete_flg: true } : item
          );
        }
      });
      dispatch(serviceMenuActions.setServiceMenuList(updatedServiceMenuList));
      await serviceMenuListService.updates(updatedServiceMenuList);
    }
  }
  async function handleConfirmDelete() {
    await processDelete();
    showGlobalModal(() => <AlertModal title={t('App.info')} message={t('serviceMenu.success_delete')} />);
    handleUnSelectAll();
  }
  function handleDeleteServiceMenuModal() {
    showGlobalModal(() => (
      <ConfirmModal
        title={t('App.confirm')}
        message={t('serviceMenu.warn_confirm_delete')}
        onConfirm={handleConfirmDelete}
      />
    ));
  }

  const handleSelectedRowChanged = useCallback(() => {
    if (btnAddRef.current && btnDeleteRef.current && btnSaveRef.current) {
      const chkSelectionElems = document.querySelectorAll<HTMLInputElement>('.chk-servicemenu-item > input:checked');
      btnDeleteRef.current.disabled = chkSelectionElems.length === 0;
    }
  }, []);

  function handleUnSelectAll() {
    const chkSelectionElems = document.querySelectorAll('.chk-servicemenu-item > input');
    chkSelectionElems.forEach(function (elem) {
      (elem as HTMLInputElement).checked = false;
    });

    if (btnDeleteRef.current) btnDeleteRef.current.disabled = true;
  }

  // handle restore video function item
  async function processRestoreServiceMenu(serviceMenuId: number) {
    try {
      const newServiceMenuList = serviceMenuList.map((item) =>
        item.id === serviceMenuId ? { ...item, delete_flg: false } : item
      );
      dispatch(serviceMenuActions.setServiceMenuList(newServiceMenuList));
      await serviceMenuService.updates(newServiceMenuList);
      showGlobalModal(() => <AlertModal title={t('App.info')} message={t('serviceMenu.success_restore')} />);
      handleUnSelectAll();
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  function handleRestoreServiceMenu(serviceMenuId: number) {
    const process = processRestoreServiceMenu.bind(null, serviceMenuId);
    process();
  }

  const onSave = async () => {
    // const modifiedData = serviceMenuList.filter((item) => item.temp_delete_flg !== item.delete_flg);

    // Check if there's no data to update
    // if (modifiedData.length === 0) {
    //   showGlobalModal(() => <AlertModal title={t('App.info')} message={t('serviceMenu.no_data_changed')} />);
    //   return;
    // }

    try {
      // Prepare data for updating
      let updateData = serviceMenuList.map((item, index) => ({
        id: item.id,
        delete_flg: item.delete_flg,
        name: item.name,
        cd: item.cd,
        priority: index + 1,
      }));

      // Send update request to the backend
      await serviceMenuService.updates(updateData);

      // Show success modal
      showGlobalModal(() => <AlertModal title={t('App.info')} message={t('serviceMenu.success_save')} />);

      // Reload updated data
      loadServiceMenuListData();
    } catch (error) {
      if (!handleServiceError(showGlobalModal, error as AxiosError)) {
        displayError(showGlobalModal, error as AxiosError);
      }
    }
  };

  async function processAdd(menuIds: number[]) {
    const maxPriority = Math.max(...serviceMenuList.map((menu) => menu.priority), 0);
    const payload: ServiceMenuAddPayload[] = menuIds.map((menuId, index) => ({
      id: menuId,
      delete_flg: false,
      priority: maxPriority + index + 1,
    }));
    return serviceMenuListService.add(payload);
  }

  function handleReflectSelectTargetMenuModal(menuIds: number[]) {
    if (processAdd && menuIds.length > 0) {
      (async () => {
        try {
          // process add data
          await processAdd(menuIds);
          // notify process success
          showGlobalModal(() => <AlertModal title={t('App.info')} message={t('serviceMenu.success_save')} />);
          // reload data
          loadServiceMenuListData();
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    }
  }

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <StatusHeader />
      </div>
      <div className={`page-main ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
        <div className="page-content store-setting-page d-flex flex-column">
          <div>
            <FeatureTitleBar title={t('serviceMenu.title')}></FeatureTitleBar>
          </div>
          <div className="label mt-4 ms-4">{t('serviceMenu.description')}</div>
          <div className="label ms-5">{t('serviceMenu.description_1')}</div>

          <div className="text-end mb-2">
            <Button ref={btnAddRef} className="btn--dark-green ms-3" onClick={handleAddServiceMenuModal}>
              {t('serviceMenu.button.add')}
            </Button>
            <Button ref={btnDeleteRef} className="btn--red ms-3 m-5 mt-0 mb-0" onClick={handleDeleteServiceMenuModal}>
              {t('serviceMenu.button.delete')}
            </Button>
            <Button ref={btnSaveRef} className="btn--dark-green ms-5" onClick={handleSubmit(onSave)}>
              {t('serviceMenu.button.save')}
            </Button>
          </div>

          <ServiceMenuListTable
            serviceMenuList={serviceMenuList}
            onRestoreServiceMenu={handleRestoreServiceMenu}
            onSelectedRowChanged={handleSelectedRowChanged}
          />

          {isModalOpen &&
            createPortal(
              <SelectTargetMenuModal
                targetSourceType={targetSourceType}
                exceptMenuIds={serviceMenuList.map((menu) => menu.id)}
                onReflect={handleReflectSelectTargetMenuModal}
                onClose={handleCloseSelectTargetMenuModal}
              />,
              document.body
            )}
        </div>
      </div>
    </div>
  );
}

export default ServiceMenu;
